import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { adminLogin, setToken, getToken } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) navigate("/control/orders", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Enter your email and password."); return; }
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      setToken(data.token);
      toast.success("Signed in");
      navigate("/control/orders", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <button type="button" onClick={() => navigate("/")} data-testid="admin-login-return" className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 transition-colors hover:bg-slate-100 hover:text-slate-900 sm:left-6 sm:top-6">
        <ArrowLeft className="h-4 w-4" /> Return to site
      </button>
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] ring-1 ring-slate-100">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-blue-600 text-white"><Lock className="h-6 w-6" /></span>
        <h1 className="mt-5 text-center font-display text-2xl font-bold text-slate-900">Admin sign in</h1>
        <p className="mt-1 text-center text-sm text-slate-500">PepsWorld UK orders dashboard</p>

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white border-slate-200" autoComplete="username" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white border-slate-200" autoComplete="current-password" />
          </div>
        </div>

        <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="mt-6 h-11 w-full bg-blue-600 font-semibold text-white hover:bg-blue-700">
          {loading ? "Signing in..." : "Sign in"}
        </Button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secure admin area</p>
      </form>
    </div>
  );
}
